import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  scan,
  shareReplay,
  Subject,
  switchMap,
} from 'rxjs';
import { ConversationChannel } from '@vendasta/conversation';
import { ConversationFilters, hasActiveInboxFilters, InboxFilters } from '../conversation-filters';
import { ConversationDetail } from '../interface/conversation.interface';
import { InboxService } from '../inbox.service';
import { ParticipantService } from '../participant.service';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN, USER_ID_TOKEN } from '../tokens';
import { ViewService } from '../view.service';
import { ConversationService } from './conversation.service';
import { ConversationPageFetcherService, ConversationChangeEvent } from './conversation-page-fetcher.service';

type AssigneeResolution = {
  assigneeUserId: string;
  fetcherFilters: Omit<InboxFilters, 'assignedToMe' | 'unassigned'>;
} | null;

@Injectable()
export class ConversationListFilteredService {
  private readonly participantService = inject(ParticipantService);
  private readonly filteredListService = inject(ConversationPageFetcherService);
  private readonly inboxService = inject(InboxService);
  private readonly conversationService = inject(ConversationService);
  private readonly viewService = inject(ViewService);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly userId$ = inject(USER_ID_TOKEN);

  // --- User inputs (set by the component) ---

  private readonly filterTerm$$ = new BehaviorSubject<string>('');
  private readonly loadMore$$ = new Subject<void>();
  private readonly refresh$$ = new Subject<void>();

  readonly selectedChannels = signal<ConversationChannel[]>([]);
  readonly unreadOnly = signal(false);
  readonly awaitingResponse = signal(false);
  readonly assignedToMe = signal(false);
  readonly unassigned = signal(false);
  readonly isClosed = signal(false);
  readonly isAnonymous = signal(false);
  readonly isInbox = signal(false);
  readonly multiViewMode = signal(false);

  // --- Derived state ---

  private readonly currentFilterTerm = toSignal(this.filterTerm$$, { initialValue: '' });
  readonly hasActiveInboxFilters = computed(() =>
    this.isClosed() ||
    this.isAnonymous() ||
    hasActiveInboxFilters({
      channels: this.selectedChannels(),
      unreadOnly: this.unreadOnly(),
      awaitingResponse: this.awaitingResponse(),
      assignedToMe: this.assignedToMe(),
      unassigned: this.unassigned(),
    }),
  );

  readonly activeInboxFilterCount = computed(
    () =>
      this.selectedChannels().length +
      (this.unreadOnly() ? 1 : 0) +
      (this.awaitingResponse() ? 1 : 0) +
      (this.assignedToMe() ? 1 : 0) +
      (this.unassigned() ? 1 : 0) +
      (this.isClosed() ? 1 : 0) +
      (this.isAnonymous() ? 1 : 0),
  );

  // --- Pass-through from other services ---

  readonly showInboxViews$ = this.inboxService.showInboxViews$;
  readonly featureFlagFullScreen$ = this.inboxService.featureFlagFullscreenInboxPC$;
  readonly canAccessSMS$ = this.inboxService.canAccessSMS$;
  readonly isBusinessApp = this.inboxService.isBusinessApp;
  readonly displayBadge$ = this.conversationService.displayBadge$;

  readonly showToolbar$ = this.viewService.selectedViewId$.pipe(map((viewId) => viewId === 'all'));

  // --- Data pipeline ---

  private readonly resolvedAccountGroupId$ = combineLatest([this.accountGroupId$, this.partnerId$]).pipe(
    map(([agId, pId]) => agId || pId),
    distinctUntilChanged(),
  );

  private readonly context$ = combineLatest([
    this.participantService.currentParticipant$,
    this.participantService.organization,
    this.resolvedAccountGroupId$,
    this.userId$,
  ]).pipe(
    map(([participant, organization, resolvedAccountGroupId, userId]) => ({
      participant,
      organization,
      resolvedAccountGroupId,
      userId,
    })),
    distinctUntilChanged((a, b) =>
      a.participant?.participantId === b.participant?.participantId &&
      a.organization?.id === b.organization?.id &&
      a.resolvedAccountGroupId === b.resolvedAccountGroupId &&
      a.userId === b.userId,
    ),
  );

  private readonly inboxFilters$ = toObservable(
    computed<InboxFilters>(() => ({
      channels: this.selectedChannels(),
      unreadOnly: this.unreadOnly(),
      awaitingResponse: this.awaitingResponse(),
      assignedToMe: this.assignedToMe(),
      unassigned: this.unassigned(),
    })),
  );

  private readonly conversationFilters$ = toObservable(
    computed<ConversationFilters>(() => {
      if (this.multiViewMode()) {
        return this.resolveMultiViewFilters(this.isInbox(), this.isAnonymous());
      }
      return {
        isAnonymous: this.isAnonymous(),
        isOpen: !this.isClosed(),
        isPrivate: false,
      };
    }),
  );

  // Multi-view is Inbox + Anonymous only — Closed cannot be combined with other views in
  // the backend query, so isOpen is always true here.
  // When both inbox and anonymous are selected, isAnonymous = undefined means "show all";
  // when only anonymous is selected, isAnonymous = true filters to anonymous only.
  private resolveMultiViewFilters(inbox: boolean, anonymous: boolean): ConversationFilters {
    const isAnonymous = inbox && anonymous ? undefined : anonymous || false;
    return { isAnonymous, isOpen: true, isPrivate: false };
  }

  private readonly scrollable$ = combineLatest([
    this.context$,
    this.conversationFilters$,
    this.filterTerm$$.pipe(debounceTime(500), distinctUntilChanged()),
    this.inboxFilters$,
  ]).pipe(
    switchMap(([ctx, filters, filterTerm, inboxFilters]) => {
      if (!ctx.participant || !ctx.resolvedAccountGroupId) {
        return of({ conversations: [] as ConversationDetail[] | null, loading: false, empty: true });
      }
      const resolved = this.resolveAssigneeFilter(inboxFilters, ctx.userId);
      if (resolved === null) {
        return of({ conversations: [] as ConversationDetail[] | null, loading: false, empty: true });
      }
      const { assigneeUserId, fetcherFilters } = resolved;
      return this.filteredListService.listConversations$(
        {
          accountGroupId: ctx.resolvedAccountGroupId,
          participantId: ctx.participant.participantId,
          organizationId: ctx.organization.id,
          filters,
          inboxFilters: fetcherFilters,
          assigneeUserId,
          filterTerm,
          groupId: null,
        },
        this.loadMore$$,
        this.refresh$$,
      ).pipe(
        catchError((err) => {
          console.error('ConversationListFilteredService: error loading conversations', err);
          return of({ conversations: [] as ConversationDetail[] | null, loading: false, empty: true });
        }),
      );
    }),
    takeUntilDestroyed(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly conversations$: Observable<ConversationDetail[] | null> = this.scrollable$.pipe(
    scan((prev, curr) => {
      // During loading, keep showing previous conversations to prevent flicker
      if (curr.loading && prev.conversations?.length) {
        return { ...curr, conversations: prev.conversations };
      }
      return curr;
    }),
    map((s) => s.conversations),
    distinctUntilChanged(),
  );

  readonly loadingConversations$: Observable<boolean> = this.scrollable$.pipe(
    map((s) => s.loading),
    distinctUntilChanged(),
  );

  readonly emptyConversations$: Observable<boolean | null> = this.scrollable$.pipe(
    map((s) => s.empty),
    filter((empty) => empty !== null),
    distinctUntilChanged(),
  );

  private readonly $emptyConversations = toSignal(this.emptyConversations$, { initialValue: false });
  private readonly $showFilters = toSignal(this.showToolbar$, { initialValue: false });

  readonly showEmptyView$ = combineLatest([this.showInboxViews$, this.emptyConversations$]).pipe(
    map(([showInboxViews, emptyConversations]) => !!(showInboxViews && emptyConversations)),
  );

  readonly hasActiveSearchTerm = computed(() => this.currentFilterTerm().trim().length > 0);

  readonly showInboxZero = computed(() => {
    const showFilters = this.$showFilters();
    const emptyConversations = this.$emptyConversations();
    const noInboxFilters = !this.hasActiveInboxFilters();
    const noSearchTerm = !this.hasActiveSearchTerm();
    return !!(showFilters && noInboxFilters && noSearchTerm && emptyConversations);
  });

  // --- Actions (called by the component) ---

  setFilterTerm(term: string): void {
    this.filterTerm$$.next(term);
  }

  loadMore(): void {
    this.loadMore$$.next();
  }

  refresh(): void {
    this.refresh$$.next();
  }

  setChannels(channels: ConversationChannel[]): void {
    this.selectedChannels.set(channels);
  }

  setUnreadOnly(value: boolean): void {
    this.unreadOnly.set(value);
  }

  setAwaitingResponse(value: boolean): void {
    this.awaitingResponse.set(value);
  }

  setAssignedToMe(value: boolean): void {
    this.assignedToMe.set(value);
  }

  setUnassigned(value: boolean): void {
    this.unassigned.set(value);
  }

  setInbox(value: boolean): void {
    this.isInbox.set(value);
  }

  setMultiViewMode(value: boolean): void {
    this.multiViewMode.set(value);
  }

  setClosed(value: boolean): void {
    this.isClosed.set(value);
  }

  setAnonymous(value: boolean): void {
    this.isAnonymous.set(value);
  }

  clearInboxFilters(): void {
    this.selectedChannels.set([]);
    this.unreadOnly.set(false);
    this.awaitingResponse.set(false);
    this.assignedToMe.set(false);
    this.unassigned.set(false);
    this.multiViewMode.set(false);
    this.isInbox.set(false);
    this.isClosed.set(false);
    this.isAnonymous.set(false);
  }

  conversationSelected(selected: boolean): void {
    this.conversationService.conversationSelected(selected);
  }

  /** The current user's participant ID — useful for building simulated change events. */
  readonly currentParticipantId$ = this.participantService.currentParticipant$.pipe(
    map((p) => p?.participantId),
  );

  /** Inject a simulated Firestore change event for testing realtime behaviors. */
  simulateRealtimeChange(docs: ConversationChangeEvent[]): void {
    this.filteredListService.simulateChange(docs);
  }

  private resolveAssigneeFilter(
    inboxFilters: InboxFilters,
    userId: string | undefined,
  ): AssigneeResolution {
    let assigneeUserId = '';
    if (inboxFilters.unassigned) {
      assigneeUserId = 'UNASSIGNED';
    } else if (inboxFilters.assignedToMe) {
      assigneeUserId = userId || '';
      if (!assigneeUserId) return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { assignedToMe, unassigned, ...fetcherFilters } = inboxFilters;
    return { assigneeUserId, fetcherFilters };
  }
}
